import { MapPin } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { typography } from '../../styles/typography';
import { Card, Input } from '../ui';
import PrimaryButton from '../ui/PrimaryButton';
import { productionApi } from './productionApi';

const getTodayDate = () => new Date().toISOString().split('T')[0];

const Production = () => {
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateDetails, setTemplateDetails] = useState(null);
  const [sites, setSites] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [bomInputs, setBomInputs] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [formData, setFormData] = useState({
    date: getTodayDate(),
    quantityProduced: '',
    batchId: '',
    labourCost: '',
    rawMaterialCost: '',
    notes: '',
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ success: '', error: '' });
  const [isLoadingSites, setIsLoadingSites] = useState(true);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isLoadingTemplateDetails, setIsLoadingTemplateDetails] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);

  // Detect mobile view
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  // Fetch sites on mount
  useEffect(() => {
    const fetchSites = async () => {
      setIsLoadingSites(true);
      try {
        const sitesData = await productionApi.getSites();
        const sitesList = Array.isArray(sitesData)
          ? sitesData
          : sitesData?.sites || sitesData?.data || [];
        
        // Sort sites alphabetically by site name (case-insensitive)
        const sortedSites = [...sitesList].sort((a, b) => {
          const nameA = (a.site_name || a.name || '').toLowerCase();
          const nameB = (b.site_name || b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
        setSites(sortedSites);
        // Don't auto-select - let user select from dropdown
      } catch (error) {
        console.error('Error fetching sites:', error);
        setStatus({
          error: 'Failed to load sites. Please refresh the page.',
          success: '',
        });
      } finally {
        setIsLoadingSites(false);
      }
    };
    fetchSites();
  }, []);

  // Fetch templates when site is selected
  useEffect(() => {
    const fetchTemplates = async () => {
      if (!selectedSite?.site_id) {
        setTemplates([]);
        return;
      }

      setIsLoadingTemplates(true);
      try {
        const result = await productionApi.getProductionTemplates({
          siteId: selectedSite.site_id || selectedSite.id,
          limit: 200,
        });
        setTemplates(result.templates || []);
      } catch (error) {
        console.error('Error fetching templates:', error);
        setStatus({
          error: 'Failed to load production templates.',
          success: '',
        });
      } finally {
        setIsLoadingTemplates(false);
      }
    };
    fetchTemplates();
  }, [selectedSite]);

  // Fetch stock items when site is selected (needed for BOM item names)
  useEffect(() => {
    const fetchStockItems = async () => {
      if (!selectedSite?.site_id) {
        setStockItems([]);
        return;
      }

      try {
        // Use stockOutApi to get stock items (same endpoint)
        const { stockOutApi } = await import('../StockOut/stockOutApi');
        const result = await stockOutApi.getStockItems({
          siteId: selectedSite.site_id || selectedSite.id,
          limit: 200,
        });
        setStockItems(result.items || []);
        console.log(
          'Production: Stock items loaded:',
          result.items?.length || 0
        );
      } catch (error) {
        console.error('Error fetching stock items:', error);
      }
    };
    fetchStockItems();
  }, [selectedSite]);

  // Fetch template details when template is selected
  useEffect(() => {
    const fetchTemplateDetails = async () => {
      if (!selectedTemplate) {
        setTemplateDetails(null);
        setBomInputs([]);
        return;
      }

      setIsLoadingTemplateDetails(true);
      try {
        const result = await productionApi.getProductionTemplate(
          selectedTemplate
        );
        console.log('Production: Template details response:', result);
        console.log('Production: Template BOM:', result.template?.bom);

        if (result.success && result.template) {
          setTemplateDetails(result.template);
          // Initialize BOM inputs from template BOM
          const bom = result.template.bom || [];

          // Map BOM items and try to find item names from stockItems
          console.log('Production: Available stock items:', stockItems);
          console.log('Production: Stock items count:', stockItems.length);

          const bomInputsWithNames = bom.map(item => {
            const stockItemId =
              item.raw_material_id || item.stock_item_id || item.item_id;

            console.log(
              'Production: Looking for stock item with ID:',
              stockItemId
            );
            console.log('Production: BOM item:', item);

            // Try to find the stock item to get its name
            const stockItem = stockItems.find(si => {
              const siId = si.stock_item_id || si.item_id;
              const matches = siId === stockItemId;
              if (matches) {
                console.log('✅ Production: Found matching stock item:', si);
              }
              return matches;
            });

            if (!stockItem) {
              console.warn(
                '⚠️ Production: Stock item not found for ID:',
                stockItemId
              );
              console.warn(
                '⚠️ Production: Available stock item IDs:',
                stockItems.map(si => si.stock_item_id || si.item_id)
              );
            }

            const itemName = stockItem
              ? stockItem.item_name || stockItem.name || 'Unknown Item'
              : item.item_name ||
                item.name ||
                item.raw_material_name ||
                'Unknown Item';

            console.log('Production: BOM item mapping result:', {
              stockItemId,
              foundStockItem: !!stockItem,
              itemName,
              stockItemName: stockItem?.item_name || stockItem?.name,
              bomItem: item,
            });

            return {
              stock_item_id: stockItemId,
              item_name: itemName,
              quantity: item.quantity || '',
              unit: item.unit || '',
              specs: item.specs || item.specification || '',
            };
          });

          console.log(
            'Production: Final BOM inputs with names:',
            bomInputsWithNames
          );
          setBomInputs(bomInputsWithNames);
        } else {
          setStatus({
            error: result.message || 'Failed to load template details.',
            success: '',
          });
        }
      } catch (error) {
        console.error('Error fetching template details:', error);
        setStatus({ error: 'Failed to load template details.', success: '' });
      } finally {
        setIsLoadingTemplateDetails(false);
      }
    };
    fetchTemplateDetails();
  }, [selectedTemplate, stockItems]);

  const handleSiteChange = siteId => {
    const site = sites.find(s => (s.site_id || s.id) === siteId);
    setSelectedSite(site || null);
    setSelectedTemplate(null);
    setTemplateDetails(null);
    setBomInputs([]);
  };

  const handleTemplateChange = templateId => {
    setSelectedTemplate(templateId || null);
  };

  const handleFormChange = field => event => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
    setStatus({ success: '', error: '' });
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!selectedSite) {
      nextErrors.site = 'Please select a site';
    }
    if (!selectedTemplate) {
      nextErrors.template = 'Please select a production template';
    }
    if (!formData.date) {
      nextErrors.date = 'Date is required';
    }

    if (
      !formData.quantityProduced ||
      parseFloat(formData.quantityProduced) <= 0
    ) {
      nextErrors.quantityProduced =
        'Quantity produced must be greater than zero';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async event => {
    event.preventDefault();

    if (!validateForm()) {
      setStatus({
        success: '',
        error: 'Please fix the highlighted fields before submitting.',
      });
      return;
    }

    setIsSubmitting(true);
    setStatus({ success: '', error: '' });

    try {
      // Step 1: Map BOM inputs to API format and create production inputs
      // Use template quantities directly since BOM is read-only
      const inputs = bomInputs
        .filter(input => input.quantity && parseFloat(input.quantity) > 0)
        .map(input => ({
          stock_item_id: input.stock_item_id,
          quantity_used: parseFloat(input.quantity), // Use template quantity
          notes: input.specs || undefined,
        }));

      // Validate that we have at least one input
      if (inputs.length === 0) {
        setStatus({
          success: '',
          error: 'Template has no valid BOM items with quantities.',
        });
        setIsSubmitting(false);
        return;
      }

      const inputsPayload = {
        production_date: formData.date,
        site_id: selectedSite.site_id || selectedSite.id,
        inputs: inputs, // API expects 'inputs' not 'lines'
      };

      console.log('Production: Submitting inputs payload:', inputsPayload);

      const inputsResult = await productionApi.createProductionInputs(
        inputsPayload
      );

      if (!inputsResult.success) {
        throw new Error(
          inputsResult.message || 'Failed to record production inputs'
        );
      }

      // Step 2: Create production run
      const runPayload = {
        production_date: formData.date,
        production_template_id: selectedTemplate, // API expects 'production_template_id' not 'template_id'
        site_id: selectedSite.site_id || selectedSite.id,
        units_produced: parseFloat(formData.quantityProduced), // API expects 'units_produced' not 'quantity_produced'
        batch_id: formData.batchId || undefined,
        labour_cost: formData.labourCost
          ? parseFloat(formData.labourCost)
          : undefined,
        raw_material_cost: formData.rawMaterialCost
          ? parseFloat(formData.rawMaterialCost)
          : undefined,
        notes: formData.notes || undefined,
      };

      console.log('Production: Submitting run payload:', runPayload);

      const runResult = await productionApi.createProductionRun(runPayload);

      if (!runResult.success) {
        throw new Error(runResult.message || 'Failed to create production run');
      }

      setStatus({
        success: 'Production entry created successfully!',
        error: '',
      });

      // Reset form
      setTimeout(() => {
        setFormData({
          date: getTodayDate(),
          quantityProduced: '',
          batchId: '',
          labourCost: '',
          rawMaterialCost: '',
          notes: '',
        });
        setSelectedTemplate(null);
        setTemplateDetails(null);
        setBomInputs([]);
        setStatus({ success: '', error: '' });
      }, 2000);
    } catch (error) {
      console.error('Error creating production entry:', error);
      setStatus({
        success: '',
        error:
          error.message ||
          'Failed to create production entry. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      date: getTodayDate(),
      quantityProduced: '',
      batchId: '',
      labourCost: '',
      rawMaterialCost: '',
      notes: '',
    });
    setSelectedTemplate(null);
    setTemplateDetails(null);
    setBomInputs([]);
    setErrors({});
    setStatus({ success: '', error: '' });
  };

  const isDirty = useMemo(() => {
    return (
      selectedTemplate !== null ||
      formData.quantityProduced !== '' ||
      formData.batchId !== '' ||
      formData.labourCost !== '' ||
      formData.notes !== ''
    );
  }, [selectedTemplate, formData]);

  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto h-auto">
        <div className="max-w-5xl space-y-8">
          <div className="text-center space-y-3">
            <h3 className={typography.h3} style={{ color: '#000000' }}>
              Production Entry
            </h3>
            <p className={typography.p} style={{ color: '#4B5563' }}>
              Record production inputs and create a production run in one form.
            </p>
          </div>

          <Card.Glassmorphism className="p-3 md:p-8 shadow-xl space-y-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Site and Template Selection */}
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label
                    htmlFor="production-site"
                    className="mb-2 block text-sm font-semibold text-gray-900"
                  >
                    <span className="inline-flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Select Site <span className="text-red-500">*</span>
                    </span>
                  </label>
                  <select
                    id="production-site"
                    value={
                      selectedSite
                        ? selectedSite.site_id || selectedSite.id
                        : ''
                    }
                    onChange={e => handleSiteChange(e.target.value)}
                    disabled={isLoadingSites}
                    className="w-full h-12 rounded-2xl border border-gray-200 bg-white px-4 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    required
                  >
                    <option value="">
                      {isLoadingSites ? 'Loading sites...' : 'Select a site'}
                    </option>
                    {/* alphabetical order */}
                    {sites.map(site => (
                      <option
                        key={site.site_id || site.id}
                        value={site.site_id || site.id}
                      >
                        {site.site_name || site.name || 'Unknown Site'}
                      </option>
                    ))}
                  </select>
                  {errors.site && (
                    <p className="mt-1 text-xs text-red-500">{errors.site}</p>
                  )}
                </div>

                {/* Production Date Selection */}
                <div>
                <label
                  htmlFor="production-date"
                  className="mb-2 block text-sm font-semibold text-gray-900"
                >
                  Production Date <span className="text-red-500">*</span>
                </label>
                <Input
                  id="production-date"
                  type="date"
                  value={formData.date}
                  onChange={handleFormChange('date')}
                  className="bg-white text-gray-900 placeholder:text-gray-500"
                  required
                />
                {errors.date && (
                  <p className="mt-1 text-xs text-red-500">{errors.date}</p>
                )}
              </div>

                {/* Production Template Selection */}
                <div>
                  <label
                    htmlFor="production-template"
                    className="mb-2 block text-sm font-semibold text-gray-900"
                  >
                    Production Template <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="production-template"
                    value={selectedTemplate || ''}
                    onChange={e => handleTemplateChange(e.target.value)}
                    disabled={!selectedSite || isLoadingTemplates}
                    className="w-full h-12 rounded-2xl border border-gray-200 bg-white px-4 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    required
                  >
                    <option value="">
                      {isLoadingTemplates
                        ? 'Loading templates...'
                        : 'Select a template'}
                    </option>
                    {templates.map(template => (
                      <option
                        key={
                          template.production_template_id ||
                          template.template_id
                        }
                        value={
                          template.production_template_id ||
                          template.template_id
                        }
                      >
                        {template.template_name ||
                          template.name ||
                          'Unknown Template'}
                      </option>
                    ))}
                  </select>
                  {errors.template && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.template}
                    </p>
                  )}
                </div>
              </div>

              {/* BOM Inputs */}
              {selectedTemplate && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">
                    Raw Materials (Bill of Materials)
                  </h4>
                  {isLoadingTemplateDetails ? (
                    <p className="text-gray-500">Loading template details...</p>
                  ) : bomInputs.length > 0 ? (
                    <div className="rounded-3xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                      <table className="min-w-full border-collapse text-sm">
                        <thead>
                          <tr className="bg-gray-50 text-left text-gray-600">
                            <th className="px-4 py-3 font-semibold">Item</th>
                            <th className="px-4 py-3 font-semibold">
                              Quantity
                            </th>
                            <th className="px-4 py-3 font-semibold">Unit</th>
                            <th className="px-4 py-3 font-semibold">
                              Specification
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {bomInputs.map((input, index) => (
                            <tr
                              key={index}
                              className="border-t border-gray-100"
                            >
                              <td className="px-4 py-3">
                                {input.item_name || 'Unknown Item'}
                              </td>
                              <td className="px-4 py-3 text-gray-700">
                                {input.quantity || '0.00'}
                              </td>
                              <td className="px-4 py-3 text-gray-700">
                                {input.unit || 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-gray-700">
                                {input.specs || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500">
                      No BOM items found for this template.
                    </p>
                  )}
                </div>
              )}

              {/* Production Run Details */}
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-4">
                  Production Run Details
                </h4>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label
                      htmlFor="production-quantity"
                      className="mb-2 block text-sm font-semibold text-gray-900"
                    >
                      Quantity Produced <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="production-quantity"
                      type="number"
                      min="0"
                      step="1"
                      placeholder="0"
                      value={formData.quantityProduced}
                      onChange={handleFormChange('quantityProduced')}
                      className="bg-white text-gray-900 placeholder:text-gray-500"
                      required
                    />
                    {errors.quantityProduced && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.quantityProduced}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="production-batch"
                      className="mb-2 block text-sm font-semibold text-gray-900"
                    >
                      Batch ID (Optional)
                    </label>
                    <Input
                      id="production-batch"
                      placeholder="Batch reference"
                      value={formData.batchId}
                      onChange={handleFormChange('batchId')}
                      className="bg-white text-gray-900 placeholder:text-gray-500"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="production-labour-cost"
                      className="mb-2 block text-sm font-semibold text-gray-900"
                    >
                      Labour Cost (Optional)
                    </label>
                    <Input
                      id="production-labour-cost"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.labourCost}
                      onChange={handleFormChange('labourCost')}
                      className="bg-white text-gray-900 placeholder:text-gray-500"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="raw_material_cost"
                      className="mb-2 block text-sm font-semibold text-gray-900"
                    >
                      Raw Material Cost (Optional)
                    </label>
                    <Input
                      id="raw_material_cost"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.rawMaterialCost}
                      onChange={handleFormChange('rawMaterialCost')}
                      className="bg-white text-gray-900 placeholder:text-gray-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label
                      htmlFor="production-notes"
                      className="mb-2 block text-sm font-semibold text-gray-900"
                    >
                      Notes (Optional)
                    </label>
                    <textarea
                      id="production-notes"
                      rows={3}
                      placeholder="Add any additional notes..."
                      value={formData.notes}
                      onChange={handleFormChange('notes')}
                      className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>
              </div>

              {status.error && (
                <p className="text-sm font-medium text-red-600">
                  {status.error}
                </p>
              )}
              {status.success && (
                <p className="text-sm font-medium text-emerald-600">
                  {status.success}
                </p>
              )}

              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-end">
                <PrimaryButton
                  type="submit"
                  disabled={isSubmitting || !selectedTemplate}
                  className={isMobileView ? 'w-full' : ''}
                >
                  {isSubmitting ? 'Creating...' : 'Create Production Entry'}
                </PrimaryButton>
              </div>
            </form>
          </Card.Glassmorphism>
        </div>
      </div>
    </div>
  );
};

export default Production;
